// src/components/common/ReviewSection.jsx
import React, { useState, useEffect } from "react";
import { Star } from "lucide-react";

// ── Static seed reviews (admin approved) ─────────────────────────────────────
const SEED_REVIEWS = [
  {
    id: "r1", name: "Anonymous", country: "India", verified: true,
    rating: 5, title: "Great", body: "Great Journal!", date: "25/07/2025", approved: true,
  },
  {
    id: "r2", name: "Yuvraj", country: "India", verified: true,
    rating: 5, title: "", body: "Good quality, little expensive but quality is good", date: "19/03/2025", approved: true,
  },
  {
    id: "r3", name: "Ankita P", country: "India", verified: true,
    rating: 1, title: "Fooling customers", body: "Deceiving customers...the Planner layout is not as shown on the website", date: "07/06/2024", approved: true,
  },
  {
    id: "r4", name: "Medini adiga", country: "India", verified: true,
    rating: 5, title: "Awesome diary", body: "Super information. A great diary where we can write down important key points to remember.", date: "12/04/2024", approved: true,
  },
  {
    id: "r5", name: "Himanshu Narang", country: "India", verified: true,
    rating: 5, title: "Can I edit after use?",
    body: "See, the thing is, you are dealing with a procrastinator here. That too a pro procrastinator. I mean, if a middle class guy spends 1.2k on a diary, then he must be freaking fed up with himself. So, to keep things short, haven't used it yet. Can write a review maybe after a month or so. Cheers :)",
    date: "12/04/2024", approved: true,
  },
];

const STORAGE_KEY = "aishcraft_reviews";

function getStoredReviews() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

function saveReview(review) {
  try {
    const existing = getStoredReviews();
    localStorage.setItem(STORAGE_KEY, JSON.stringify([...existing, review]));
  } catch {}
}

// ── Stars display ─────────────────────────────────────────────────────────────
function StarDisplay({ value, size = 16 }) {
  return (
    <div className="flex gap-0.5">
      {[1,2,3,4,5].map(n => (
        <Star key={n} size={size}
          className={n <= value ? "fill-black text-black" : "fill-none text-black/20"}/>
      ))}
    </div>
  );
}

// ── Star picker (for form) ────────────────────────────────────────────────────
function StarPicker({ value, onChange }) {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex gap-1">
      {[1,2,3,4,5].map(n => (
        <button key={n} type="button"
          onMouseEnter={() => setHover(n)} onMouseLeave={() => setHover(0)}
          onClick={() => onChange(n)}>
          <Star size={24}
            className={(hover || value) >= n ? "fill-black text-black" : "fill-none text-black/30"}/>
        </button>
      ))}
    </div>
  );
}

// ── Single review card ────────────────────────────────────────────────────────
function ReviewCard({ review }) {
  const [expanded, setExpanded] = useState(false);
  const LIMIT = 200;
  const long = review.body.length > LIMIT;
  const displayBody = expanded || !long ? review.body : review.body.slice(0, LIMIT) + "...";

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-5 flex flex-col gap-3">
      {/* Top row */}
      <div className="flex items-start justify-between gap-2">
        <StarDisplay value={review.rating} size={15}/>
        <span className="text-xs text-gray-400 flex-shrink-0">{review.date}</span>
      </div>

      {/* Reviewer info */}
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-full bg-gray-100 border border-gray-200 flex items-center justify-center flex-shrink-0">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="w-5 h-5 text-gray-500">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
          </svg>
        </div>
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold">{review.name}</span>
            {review.verified && (
              <span className="bg-black text-white text-[9px] font-bold px-1.5 py-0.5 rounded-sm tracking-wide">Verified</span>
            )}
          </div>
          <div className="flex items-center gap-1 mt-0.5">
            <svg viewBox="0 0 24 24" className="w-3 h-3 text-gray-400" fill="currentColor">
              <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
            <span className="text-[11px] text-gray-400">{review.country}</span>
          </div>
        </div>
      </div>

      {/* Review content */}
      <div>
        {review.title && <p className="text-sm font-bold mb-1">{review.title}</p>}
        <p className="text-sm text-gray-700 leading-relaxed">{displayBody}</p>
        {long && (
          <button onClick={() => setExpanded(e => !e)}
            className="text-sm font-bold mt-1 hover:underline">
            {expanded ? "Show less" : "Read more"}
          </button>
        )}
      </div>
    </div>
  );
}

// ── Write Review Form ─────────────────────────────────────────────────────────
function WriteReviewForm({ productSlug, onSubmit }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", rating: 0, title: "", body: "" });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.rating) return setError("Please select a star rating.");
    if (!form.name.trim()) return setError("Please enter your name.");
    if (!form.body.trim()) return setError("Please write your review.");
    setError("");

    const review = {
      id: `user-${Date.now()}`,
      name: form.name.trim(),
      country: "India",
      verified: false,
      rating: form.rating,
      title: form.title.trim(),
      body: form.body.trim(),
      date: new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "2-digit", year: "numeric" }).replace(/\//g, "/"),
      approved: false, // admin approval required
      productSlug,
    };

    saveReview(review);
    onSubmit(review);
    setSubmitted(true);
    setForm({ name: "", rating: 0, title: "", body: "" });
  };

  if (!open) return (
    <button onClick={() => setOpen(true)}
      className="mt-2 px-6 py-2.5 border-2 border-black rounded-full text-sm font-semibold hover:bg-black hover:text-white transition-colors">
      Write a Review
    </button>
  );

  if (submitted) return (
    <div className="mt-4 border border-green-200 bg-green-50 rounded-lg p-5 text-center">
      <p className="text-sm font-semibold text-green-700">✓ Thank you! Your review has been submitted.</p>
      <p className="text-xs text-green-600 mt-1">It will appear after admin approval.</p>
      <button onClick={() => { setSubmitted(false); setOpen(false); }}
        className="mt-3 text-xs underline text-green-700">Close</button>
    </div>
  );

  return (
    <form onSubmit={handleSubmit} className="mt-4 border border-gray-200 rounded-lg p-5 flex flex-col gap-4 bg-white">
      <div className="flex items-center justify-between">
        <h3 className="font-bold text-sm">Write a Review</h3>
        <button type="button" onClick={() => setOpen(false)} className="text-gray-400 hover:text-gray-700 text-lg">×</button>
      </div>

      {/* Rating */}
      <div>
        <label className="text-xs font-semibold text-gray-600 mb-1.5 block">Your Rating *</label>
        <StarPicker value={form.rating} onChange={v => set("rating", v)}/>
      </div>

      {/* Name */}
      <div>
        <label className="text-xs font-semibold text-gray-600 mb-1.5 block">Your Name *</label>
        <input value={form.name} onChange={e => set("name", e.target.value)}
          placeholder="Enter your name"
          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:border-black focus:outline-none"/>
      </div>

      {/* Title */}
      <div>
        <label className="text-xs font-semibold text-gray-600 mb-1.5 block">Review Title</label>
        <input value={form.title} onChange={e => set("title", e.target.value)}
          placeholder="Summary of your experience (optional)"
          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:border-black focus:outline-none"/>
      </div>

      {/* Body */}
      <div>
        <label className="text-xs font-semibold text-gray-600 mb-1.5 block">Your Review *</label>
        <textarea value={form.body} onChange={e => set("body", e.target.value)}
          placeholder="Share your experience with this product..."
          rows={4}
          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:border-black focus:outline-none resize-none"/>
      </div>

      {error && <p className="text-xs text-red-500">{error}</p>}

      <p className="text-[11px] text-gray-400">Your review will appear after admin approval.</p>

      <button type="submit"
        className="w-full bg-black text-white rounded-md py-2.5 text-sm font-semibold hover:opacity-90 transition-opacity">
        Submit Review
      </button>
    </form>
  );
}

// ── Rating summary bar ────────────────────────────────────────────────────────
function RatingSummary({ reviews }) {
  const total = reviews.length;
  const avg = total ? (reviews.reduce((s, r) => s + r.rating, 0) / total).toFixed(1) : 0;
  const counts = [5,4,3,2,1].map(n => ({ n, count: reviews.filter(r => r.rating === n).length }));

  return (
    <div className="flex items-start gap-8 mb-8 pb-8 border-b border-gray-100">
      {/* Big number */}
      <div className="text-center flex-shrink-0">
        <p className="text-5xl font-bold leading-none">{avg}</p>
        <StarDisplay value={Math.round(avg)} size={14}/>
        <p className="text-xs text-gray-400 mt-1">{total} reviews</p>
      </div>
      {/* Bars */}
      <div className="flex-1 flex flex-col gap-1.5">
        {counts.map(({ n, count }) => (
          <div key={n} className="flex items-center gap-2">
            <span className="text-xs text-gray-500 w-3">{n}</span>
            <Star size={11} className="fill-black text-black flex-shrink-0"/>
            <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
              <div className="h-full bg-black rounded-full transition-all" style={{ width: total ? `${(count/total)*100}%` : "0%" }}/>
            </div>
            <span className="text-xs text-gray-400 w-4">{count}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Main export ───────────────────────────────────────────────────────────────
export default function ReviewSection({ productSlug }) {
  const [pendingReview, setPendingReview] = useState(null);

  // Combine seed + approved stored reviews
  const stored = getStoredReviews().filter(r => r.productSlug === productSlug && r.approved);
  const allReviews = [...SEED_REVIEWS, ...stored];

  return (
    <section className="bg-[#FAFAFA] py-16 px-4">
      <div className="mx-auto max-w-5xl">
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-2xl font-bold">Customer Reviews</h2>
        </div>

        <RatingSummary reviews={allReviews}/>

        {/* Review grid — masonry-like 3 col */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
          {allReviews.map(review => (
            <ReviewCard key={review.id} review={review}/>
          ))}
          {/* Pending review preview */}
          {pendingReview && (
            <div className="relative">
              <ReviewCard review={{...pendingReview, name: pendingReview.name + " (pending)"}}/>
              <div className="absolute inset-0 bg-white/70 rounded-lg flex items-center justify-center">
                <span className="text-xs font-semibold text-gray-500 bg-white px-3 py-1 rounded-full border border-gray-200">Pending approval</span>
              </div>
            </div>
          )}
        </div>

        <WriteReviewForm productSlug={productSlug}
          onSubmit={(r) => setPendingReview(r)}/>
      </div>
    </section>
  );
}